/* Diwali sale landing page: lighting, sparks, fireworks, countdowns, gift box, rail and gift finder. */
(() => {
  const html = document.documentElement;
  html.classList.add('dw-js', 'dw-ready');

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const UAE_TIME = '+04:00';
  const DEFAULT_START = Date.parse('2026-10-20T00:00:00+04:00');
  const DEFAULT_END = Date.parse('2026-11-08T23:59:00+04:00');

  /* ---------- Dates ---------- */

  // "2026-10-20 00:00" (UAE time unless an offset is given) → timestamp
  const parseDate = (text, fallback) => {
    let iso = (text || '').trim().replace(' ', 'T');
    if (!iso) return fallback;
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) iso += 'T00:00';
    if (/T\d{2}:\d{2}$/.test(iso)) iso += ':00';
    if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(iso)) iso += UAE_TIME;
    const time = Date.parse(iso);
    return Number.isNaN(time) ? fallback : time;
  };

  const saleWindow = () => {
    const el = document.querySelector('[data-dw-sale]');
    return {
      start: parseDate(el && el.dataset.start, DEFAULT_START),
      end: parseDate(el && el.dataset.end, DEFAULT_END)
    };
  };

  /* ---------- Countdowns (every one on the page, one timer) ---------- */

  const pad = (n) => String(n).padStart(2, '0');
  const LABELS = { before: 'Sale starts in', live: 'Sale ends in', ended: 'The Diwali sale has ended' };

  function tickCountdowns() {
    const { start, end } = saleWindow();
    const now = Date.now();
    const state = now < start ? 'before' : now < end ? 'live' : 'ended';
    const left = Math.max(0, Math.floor(((state === 'before' ? start : end) - now) / 1000));
    const parts = {
      d: Math.floor(left / 86400),
      h: Math.floor((left % 86400) / 3600),
      m: Math.floor((left % 3600) / 60),
      s: left % 60
    };
    document.querySelectorAll('[data-dw-countdown]').forEach((count) => {
      count.classList.toggle('is-ended', state === 'ended');
      const label = count.querySelector('[data-dw-count-label]');
      if (label && label.textContent !== LABELS[state]) label.textContent = LABELS[state];
      const compact = count.classList.contains('dw-count--compact');
      Object.entries(parts).forEach(([unit, value]) => {
        const el = count.querySelector(`[data-u="${unit}"]`);
        const text = pad(value);
        if (!el || el.textContent === text) return;
        el.textContent = text;
        if (!reduced && !compact && unit === 's') {
          el.classList.remove('is-tick');
          void el.offsetWidth;
          el.classList.add('is-tick');
        }
      });
    });
  }

  /* ---------- Particles: sparks, embers and fireworks share one small engine ---------- */

  const COLORS = {
    gold: [42, 92, 70],
    flame: [30, 96, 60],
    rose: [345, 88, 70],
    ivory: [40, 100, 92],
    ruby: [352, 86, 60],
    amber: [36, 100, 64]
  };
  const pick = (list) => list[Math.floor(Math.random() * list.length)];
  const rand = (a, b) => a + Math.random() * (b - a);

  class Sky {
    constructor(canvas, { fixed = false } = {}) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.fixed = fixed;
      this.particles = [];
      this.running = false;
      this.keepAlive = () => false;
      this.update = null;
      this.resize();
      window.addEventListener('resize', () => this.resize(), { passive: true });
    }

    resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = this.fixed ? window.innerWidth : this.canvas.clientWidth;
      const h = this.fixed ? window.innerHeight : this.canvas.clientHeight;
      this.w = w;
      this.h = h;
      this.scale = Math.min(1, 0.45 + w / 2200);
      this.canvas.width = Math.max(1, Math.round(w * dpr));
      this.canvas.height = Math.max(1, Math.round(h * dpr));
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    add(p) {
      this.particles.push(
        Object.assign({ life: 0, max: 1, vx: 0, vy: 0, g: 0, drag: 1, size: 1.6, trail: 3, color: COLORS.gold }, p)
      );
      this.start();
    }

    start() {
      if (this.running || reduced) return;
      this.running = true;
      this.last = performance.now();
      requestAnimationFrame(this.frame);
    }

    frame = (now) => {
      const dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      if (this.update) this.update(dt);

      const { ctx, particles } = this;
      ctx.clearRect(0, 0, this.w, this.h);
      ctx.globalCompositeOperation = 'lighter';
      ctx.lineCap = 'round';

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.life += dt;
        if (p.life >= p.max) {
          particles.splice(i, 1);
          if (p.onDie) p.onDie(p);
          continue;
        }
        const drag = Math.pow(p.drag, dt * 60);
        p.vx *= drag;
        p.vy = p.vy * drag + p.g * dt;
        const px = p.x;
        const py = p.y;
        p.x += p.vx * dt;
        p.y += p.vy * dt;

        if (p.emit && Math.random() < 0.8) {
          this.add({ x: p.x, y: p.y, vx: rand(-20, 20), vy: rand(10, 50), g: 120, max: rand(0.25, 0.5), size: 1, trail: 1.5, color: COLORS.amber });
        }

        const k = 1 - p.life / p.max;
        let alpha = p.fade === false ? 1 : Math.min(1, k * 1.6);
        if (p.flicker && k < 0.6) alpha *= Math.random() < 0.5 ? 1 : 0.15;
        const [h, s, l] = p.color;
        ctx.strokeStyle = `hsla(${h}, ${s}%, ${l + (1 - k) * 8}%, ${alpha})`;
        ctx.lineWidth = Math.max(0.6, p.size * (0.45 + 0.55 * k));
        ctx.beginPath();
        ctx.moveTo(px - (p.x - px) * p.trail, py - (p.y - py) * p.trail);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
      }

      if (particles.length || this.keepAlive()) {
        requestAnimationFrame(this.frame);
      } else {
        this.running = false;
        ctx.clearRect(0, 0, this.w, this.h);
      }
    };

    burst(x, y, { count = 60, speed = 260, colors = ['gold'], ring = false, life = [1, 1.6], drag = 0.975, g = 100, size = 2, trail = 4, flicker = false } = {}) {
      const s = speed * this.scale;
      for (let i = 0; i < count; i++) {
        const angle = ring ? (i / count) * Math.PI * 2 : Math.random() * Math.PI * 2;
        const v = ring ? s * rand(0.96, 1.04) : s * Math.sqrt(Math.random());
        this.add({
          x,
          y,
          vx: Math.cos(angle) * v,
          vy: Math.sin(angle) * v,
          g,
          drag,
          max: rand(life[0], life[1]),
          size,
          trail,
          flicker,
          color: COLORS[pick(colors)]
        });
      }
    }

    // A rocket that rises to (tx, ty) and explodes there
    launch(tx, ty) {
      const g = 420;
      const x = tx != null ? tx : this.w * rand(0.14, 0.86);
      const y = ty != null ? ty : this.h * rand(0.12, 0.42);
      const sx = x + rand(-0.05, 0.05) * this.w;
      const rise = Math.max(60, this.h - y);
      const vy = -Math.sqrt(2 * g * rise);
      const t = -vy / g;
      this.add({
        x: sx,
        y: this.h,
        vx: (x - sx) / t,
        vy,
        g,
        drag: 1,
        max: t * 0.97,
        size: 2.2,
        trail: 3,
        fade: false,
        emit: true,
        color: COLORS.ivory,
        onDie: (p) => this.explode(p.x, p.y)
      });
    }

    explode(x, y) {
      const kind = pick(['peony', 'peony', 'ring', 'willow', 'crackle']);
      const hues = pick([['gold', 'amber'], ['rose', 'ivory'], ['ruby', 'gold'], ['flame', 'gold'], ['ivory', 'amber']]);
      if (kind === 'ring') {
        this.burst(x, y, { count: 54, speed: 250, ring: true, colors: hues, life: [1.2, 1.5], g: 70 });
        this.burst(x, y, { count: 20, speed: 90, colors: ['ivory'], life: [0.6, 0.9] });
      } else if (kind === 'willow') {
        this.burst(x, y, { count: 70, speed: 190, colors: ['gold', 'amber'], life: [2.2, 3], drag: 0.962, g: 55, size: 1.7, trail: 6 });
      } else if (kind === 'crackle') {
        this.burst(x, y, { count: 60, speed: 230, colors: ['ivory', 'gold'], life: [0.9, 1.3], flicker: true, size: 1.8 });
      } else {
        this.burst(x, y, { count: 90, speed: 290, colors: hues, life: [1.1, 1.8] });
      }
    }
  }

  // One page-wide canvas above everything, for add-to-bag and gift-box sparkles
  let overlay = null;
  const overlaySky = () => {
    if (overlay) return overlay;
    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:2147483000;';
    document.body.appendChild(canvas);
    overlay = new Sky(canvas, { fixed: true });
    return overlay;
  };
  const sparkleAt = (el, opts = {}) => {
    if (reduced || !el) return;
    const r = el.getBoundingClientRect();
    const sky = overlaySky();
    sky.burst(r.left + r.width / 2, r.top + (opts.top != null ? r.height * opts.top : r.height / 2), {
      count: 34,
      speed: 230,
      colors: ['gold', 'amber', 'ivory'],
      life: [0.5, 0.95],
      g: 340,
      size: 1.8,
      trail: 3,
      ...opts
    });
  };

  /* ---------- Shared: reveal on scroll ---------- */

  const revealer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-in');
              revealer.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
        )
      : null;

  function initReveals(root) {
    root.querySelectorAll('[data-dw-reveal]').forEach((el) => {
      if (el.parentElement && el.parentElement.matches('[data-dw-rail-track]')) {
        el.style.setProperty('--i', Array.prototype.indexOf.call(el.parentElement.children, el) % 5);
      }
      if (revealer && !reduced) revealer.observe(el);
      else el.classList.add('is-in');
    });
  }

  const watch = (el, onChange, options) => {
    if (!('IntersectionObserver' in window)) {
      onChange(true);
      return;
    }
    new IntersectionObserver((entries) => entries.forEach((e) => onChange(e.isIntersecting, e)), options).observe(el);
  };

  /* ---------- Hero ---------- */

  /* ---------- Golden fragrance mist ---------- */

  // Soft puffs of warm gold rise from the bottom of the hero, swell, turn slowly and fade, like perfume
  // vapour in lamplight. Drawn at half resolution (it is all soft focus) and paused off screen.
  function initMist(hero) {
    const canvas = hero.querySelector('[data-dw-mist]');
    if (!canvas || reduced) return;
    const ctx = canvas.getContext('2d');
    const RES = 0.5;

    // A few cloudy sprites, each built from many faint overlapping blobs so the mist looks wispy
    const sprites = [0, 1, 2].map(() => {
      const size = 256;
      const sprite = document.createElement('canvas');
      sprite.width = sprite.height = size;
      const g = sprite.getContext('2d');
      for (let i = 0; i < 26; i++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = Math.sqrt(Math.random()) * size * 0.26;
        const x = size / 2 + Math.cos(angle) * dist;
        const y = size / 2 + Math.sin(angle) * dist;
        const r = size * rand(0.1, 0.24);
        const blob = g.createRadialGradient(x, y, 0, x, y, r);
        blob.addColorStop(0, `rgba(255, ${Math.round(rand(200, 228))}, ${Math.round(rand(140, 175))}, 0.22)`);
        blob.addColorStop(1, 'rgba(255, 210, 150, 0)');
        g.fillStyle = blob;
        g.fillRect(0, 0, size, size);
      }
      return sprite;
    });

    let w = 0;
    let h = 0;
    let scale = 1;
    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      scale = Math.max(0.55, Math.min(1.2, w / 1200));
      canvas.width = Math.max(1, Math.round(w * RES));
      canvas.height = Math.max(1, Math.round(h * RES));
    };
    resize();
    // the hero sizes itself to the screen after this runs, so follow the canvas, not just the window
    if ('ResizeObserver' in window) new ResizeObserver(resize).observe(canvas);
    else window.addEventListener('resize', resize, { passive: true });

    // Puffs gather towards the middle and under the two lit windows
    const spawnX = () => {
      const r = Math.random();
      if (r < 0.45) return w * (0.5 + (Math.random() + Math.random() - 1) * 0.22);
      if (r < 0.7) return w * rand(0.04, 0.3);
      if (r < 0.95) return w * rand(0.7, 0.96);
      return w * Math.random();
    };
    const make = (age) => {
      const max = rand(11, 17);
      return {
        x: spawnX(),
        y: h + 40,
        vy: -rand(14, 30),
        size: rand(150, 260) * scale,
        grow: rand(1.8, 2.8),
        rot: Math.random() * Math.PI * 2,
        spin: rand(-0.12, 0.12),
        phase: Math.random() * Math.PI * 2,
        sway: rand(6, 16),
        sprite: sprites[Math.floor(Math.random() * sprites.length)],
        life: age,
        max
      };
    };
    const count = window.innerWidth < 750 ? 14 : 26;
    // start with the mist already risen, at every stage of its life
    const puffs = Array.from({ length: count }, () => {
      const p = make(0);
      p.life = Math.random() * p.max;
      p.y = h + 40 + p.vy * p.life;
      return p;
    });

    let running = false;
    let visible = true;
    let last = 0;
    const frame = (now) => {
      if (!visible) {
        running = false;
        return;
      }
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      ctx.setTransform(RES, 0, 0, RES, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'screen';
      puffs.forEach((p, i) => {
        p.life += dt;
        if (p.life >= p.max) {
          puffs[i] = make(0);
          return;
        }
        const k = p.life / p.max;
        p.y += p.vy * dt;
        p.rot += p.spin * dt;
        const x = p.x + Math.sin(p.phase + p.life * 0.5) * p.sway;
        const size = p.size * (1 + k * (p.grow - 1));
        ctx.globalAlpha = Math.sin(Math.PI * k) * 0.42;
        ctx.save();
        ctx.translate(x, p.y);
        ctx.rotate(p.rot);
        ctx.drawImage(p.sprite, -size / 2, -size / 2, size, size);
        ctx.restore();
      });
      ctx.globalAlpha = 1;
      requestAnimationFrame(frame);
    };
    const start = () => {
      if (running) return;
      running = true;
      last = performance.now();
      requestAnimationFrame(frame);
    };
    watch(hero, (isVisible) => {
      visible = isVisible;
      if (visible) start();
    });
    start();
  }

  function initHero(hero) {
    hero.querySelectorAll('[data-dw-step]').forEach((el, i) => el.style.setProperty('--s', i));
    initMist(hero);

    // Fit the opening scene to the screen below the store header, so the row of diyas sits on the fold.
    // The text block is zoomed down a little on short screens rather than pushing the diyas out of view.
    const inner = hero.querySelector('.dw-hero__inner');
    let fittedWidth = 0;
    const fit = () => {
      if (!inner || window.innerWidth === fittedWidth) return;
      fittedWidth = window.innerWidth;
      inner.style.removeProperty('--dw-fit');
      hero.style.removeProperty('min-height');
      const top = hero.getBoundingClientRect().top + window.scrollY;
      const room = window.innerHeight - top;
      if (room < 480) return;
      const cs = getComputedStyle(hero);
      const padding = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      // never shrink the words below 86%: readable beats a perfect fit
      const scale = Math.max(0.86, Math.min(1, (room - padding) / inner.offsetHeight));
      inner.style.setProperty('--dw-fit', scale.toFixed(3));
      hero.style.minHeight = `${Math.round(room)}px`;
    };
    fit();
    window.addEventListener('resize', fit, { passive: true });

    const canvas = hero.querySelector('[data-dw-sparks]');
    const hint = hero.querySelector('[data-dw-hint]');
    const sky = canvas && !reduced ? new Sky(canvas) : null;
    let visible = true;
    let lit = false;
    let flames = [];

    const measureFlames = () => {
      const base = hero.getBoundingClientRect();
      flames = Array.from(hero.querySelectorAll('.dw-hero__ledge .dw-diya__flame'))
        .filter((f) => f.offsetParent !== null)
        .map((f) => {
          const r = f.getBoundingClientRect();
          return { x: r.left - base.left + r.width / 2, y: r.top - base.top + r.height * 0.15, delay: parseFloat(getComputedStyle(f.parentElement).getPropertyValue('--delay')) || 0 };
        });
    };

    const light = () => {
      hero.classList.add('is-lit');
      if (!sky) return;
      measureFlames();
      // a little spark as each diya catches
      flames.forEach((f) => {
        setTimeout(() => {
          sky.burst(f.x, f.y, { count: 14, speed: 120, colors: ['ivory', 'gold'], life: [0.35, 0.7], g: 220, size: 1.4, trail: 2 });
        }, (f.delay + 0.08) * 1000);
      });
    };

    if (sky) {
      let emberClock = 0;
      sky.keepAlive = () => visible && lit;
      sky.update = (dt) => {
        if (!visible || !flames.length) return;
        emberClock += dt;
        const every = window.innerWidth < 750 ? 0.16 : 0.09;
        while (emberClock > every) {
          emberClock -= every;
          const f = pick(flames);
          sky.add({
            x: f.x + rand(-4, 4),
            y: f.y,
            vx: rand(-14, 14),
            vy: rand(-70, -30),
            g: -6,
            drag: 0.995,
            max: rand(1.8, 3.6),
            size: rand(1, 1.8),
            trail: 1.4,
            color: COLORS[pick(['flame', 'gold', 'amber'])]
          });
        }
      };
      setTimeout(() => {
        lit = true;
        sky.start();
      }, 900);

      // Phuljhadi: sparks trail the cursor or finger across the night
      let lastX = null;
      let lastY = null;
      const sparkle = (clientX, clientY) => {
        const base = hero.getBoundingClientRect();
        const x = clientX - base.left;
        const y = clientY - base.top;
        if (lastX !== null && Math.hypot(x - lastX, y - lastY) < 6) return;
        lastX = x;
        lastY = y;
        const n = 4 + Math.floor(Math.random() * 3);
        for (let i = 0; i < n; i++) {
          const a = Math.random() * Math.PI * 2;
          const v = rand(80, 280);
          sky.add({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, g: 320, drag: 0.96, max: rand(0.3, 0.7), size: rand(1, 1.7), trail: 2.6, flicker: true, color: COLORS[pick(['ivory', 'gold', 'amber'])] });
        }
        if (hint) hint.classList.add('is-gone');
      };
      hero.addEventListener('pointermove', (e) => {
        if (e.pointerType === 'mouse') sparkle(e.clientX, e.clientY);
      });
      hero.addEventListener(
        'touchmove',
        (e) => {
          const t = e.touches[0];
          if (t) sparkle(t.clientX, t.clientY);
        },
        { passive: true }
      );
      hero.addEventListener('pointerleave', () => {
        lastX = lastY = null;
        hero.querySelectorAll('.dw-note.is-near').forEach((note) => note.classList.remove('is-near'));
      });

      // A scent note brightens as the cursor passes near it
      const notes = Array.from(hero.querySelectorAll('.dw-note'));
      let nearQueued = false;
      let pointer = null;
      if (notes.length) {
        hero.addEventListener('pointermove', (e) => {
          if (e.pointerType !== 'mouse') return;
          pointer = e;
          if (nearQueued) return;
          nearQueued = true;
          requestAnimationFrame(() => {
            nearQueued = false;
            notes.forEach((note) => {
              const r = note.getBoundingClientRect();
              const near = r.width > 0 && Math.hypot(pointer.clientX - (r.left + r.width / 2), pointer.clientY - (r.top + r.height / 2)) < 110;
              note.classList.toggle('is-near', near);
            });
          });
        });
      }

      watch(hero, (isVisible) => {
        visible = isVisible;
        if (visible) sky.start();
      });

      // Once the diyas are lit, a phuljhadi sweeps across the night by itself, so people know to try it
      setTimeout(() => {
        if (!visible || lastX !== null) return;
        const base = hero.getBoundingClientRect();
        const start = performance.now();
        const sweep = (now) => {
          const t = Math.min(1, (now - start) / 1400);
          const ease = 1 - Math.pow(1 - t, 2);
          const x = base.left + base.width * (0.12 + 0.76 * ease);
          const y = base.top + base.height * (0.62 - Math.sin(ease * Math.PI) * 0.34);
          sparkle(x, y);
          if (t < 1) requestAnimationFrame(sweep);
          else lastX = lastY = null;
        };
        requestAnimationFrame(sweep);
      }, 3000);
      window.addEventListener('resize', measureFlames, { passive: true });
    }

    if (reduced) light();
    else requestAnimationFrame(() => requestAnimationFrame(light));
  }

  /* ---------- Product rail ---------- */

  function initRail(section) {
    const track = section.querySelector('[data-dw-rail-track]');
    if (!track) return;
    const nav = section.querySelector('[data-dw-rail-nav]');
    const prev = section.querySelector('[data-dw-rail-prev]');
    const next = section.querySelector('[data-dw-rail-next]');
    const bar = section.querySelector('[data-dw-rail-bar]');

    const step = () => {
      const cell = Array.from(track.children).find((child) => child.offsetWidth > 0);
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return cell ? (cell.getBoundingClientRect().width + gap) * Math.max(1, Math.floor(track.clientWidth / (cell.getBoundingClientRect().width + gap)) - 1) : 300;
    };
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const visible = track.clientWidth / track.scrollWidth;
      if (nav) nav.classList.toggle('is-scrollable', max > 4);
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
      if (bar) {
        bar.style.width = `${Math.min(100, visible * 100)}%`;
        bar.style.transform = `translateX(${max > 0 ? (track.scrollLeft / max) * (1 / visible - 1) * 100 : 0}%)`;
      }
    };
    const behavior = reduced ? 'auto' : 'smooth';
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior }));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* ---------- Gift box ---------- */

  function initGifts(section) {
    const box = section.querySelector('[data-dw-giftbox]');
    const cells = Array.from(section.querySelectorAll('[data-dw-gift-card]'));
    if (!box) {
      cells.forEach((c) => c.classList.add('is-out'));
      return;
    }
    let opened = false;

    const shower = () => sparkleAt(box, { top: 0.3, count: 60, speed: 340, life: [0.7, 1.3], g: 300, colors: ['gold', 'amber', 'ivory', 'rose'] });

    const flyOut = () => {
      const b = box.getBoundingClientRect();
      const bx = b.left + b.width / 2;
      const by = b.top + b.height * 0.35;
      cells.forEach((cell, i) => {
        cell.classList.add('is-out');
        if (reduced || !cell.animate) return;
        const r = cell.getBoundingClientRect();
        const dx = bx - (r.left + r.width / 2);
        const dy = by - (r.top + r.height / 2);
        cell.animate(
          [
            { transform: `translate(${dx}px, ${dy}px) scale(0.16) rotate(${i % 2 ? 16 : -16}deg)`, opacity: 0 },
            { opacity: 1, offset: 0.2 },
            { transform: 'none', opacity: 1 }
          ],
          { duration: 1150, delay: 650 + i * 120, easing: 'cubic-bezier(.18,.9,.25,1.04)', fill: 'backwards' }
        );
      });
    };

    const open = () => {
      if (opened) {
        shower();
        return;
      }
      opened = true;
      box.classList.add('is-open');
      box.setAttribute('aria-label', 'Gift box opened');
      setTimeout(shower, reduced ? 0 : 620);
      flyOut();
    };

    box.addEventListener('click', open);
    if (reduced) {
      open();
      return;
    }
    // Open once the box is in the upper half of the screen, or as soon as any gift card shows up
    watch(box, (isVisible) => isVisible && !opened && setTimeout(open, 250), { rootMargin: '0px 0px -42% 0px' });
    cells.forEach((cell) => watch(cell, (isVisible) => isVisible && !opened && open(), { threshold: 0.6 }));
  }

  /* ---------- Gift finder ---------- */

  // whole amounts in the store's own money format (e.g. "Dhs. 120"), as the cards print them
  const money = (cents) => {
    const whole = Math.round(cents / 100) * 100;
    const fox = window.FoxTheme;
    if (!fox || !fox.Currency || !fox.settings) return (whole / 100).toLocaleString('en');
    return fox.Currency.formatMoney(whole, fox.settings.moneyFormat).replace(/<[^>]*>/g, '').replace(/([.,])00(?!\d)/, '');
  };

  function initFinder(section) {
    // the border lights twinkle only while the section is on screen
    if (section.querySelector('.dw-bulbs')) watch(section, (isVisible) => section.classList.toggle('dw-lights-on', isVisible));
    const grid = section.querySelector('[data-dw-grid]');
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll('[data-dw-card]'));
    const seg = section.querySelector('[data-dw-seg]');
    const segThumb = section.querySelector('[data-dw-seg-thumb]');
    const whoButtons = Array.from(section.querySelectorAll('[data-dw-who]'));
    const vibes = Array.from(section.querySelectorAll('[data-dw-vibe]'));
    const range = section.querySelector('[data-dw-budget]');
    const rangeValue = section.querySelector('[data-dw-budget-value]');
    const rangeMin = section.querySelector('[data-dw-budget-min]');
    const rangeMax = section.querySelector('[data-dw-budget-max]');
    const sortSelect = section.querySelector('[data-dw-sort]');
    const result = section.querySelector('[data-dw-count]');
    const pick = section.querySelector('[data-dw-pick]');
    const more = section.querySelector('[data-dw-more]');
    const moreN = section.querySelector('[data-dw-more-n]');
    const empty = section.querySelector('[data-dw-empty]');
    const reset = section.querySelector('[data-dw-reset]');
    // In a sideways row every match is shown; a grid shows a page at a time behind "Show more"
    const step = grid.hasAttribute('data-dw-rail-track') ? Infinity : parseInt(section.dataset.initial, 10) || 12;
    if (!cards.length) return;
    initRail(section);
    const toStart = () => {
      grid.scrollLeft = 0;
      grid.dispatchEvent(new Event('scroll'));
    };

    // The slider runs from the cheapest to the dearest live price, in 10 Dhs steps
    const prices = cards.map((card) => Number(card.dataset.price));
    const floor = Math.floor(Math.min(...prices) / 1000) * 1000;
    const ceil = Math.ceil(Math.max(...prices) / 1000) * 1000;
    range.min = String(floor);
    range.max = String(ceil);
    range.value = String(ceil);
    rangeMin.textContent = money(floor);
    rangeMax.textContent = money(ceil);

    let who = whoButtons[0] || null;
    let vibe = vibes[0] || null;
    let sort = 'featured';
    let limit = step;

    const inWho = (card, button) => {
      const value = button ? button.dataset.dwWho : 'all';
      return value === 'all' || (card.dataset.for || '').split(' ').includes(value);
    };
    const inVibe = (card, button) => {
      const value = button ? button.dataset.dwVibe : 'all';
      return value === 'all' || (card.dataset.vibes || '').split(' ').includes(value);
    };
    const budget = () => Number(range.value);
    const inBudget = (card) => Number(card.dataset.price) <= budget();
    const isAny = () => budget() >= ceil;
    const matches = () => cards.filter((card) => inWho(card, who) && inVibe(card, vibe) && inBudget(card));

    // A vibe that fits nothing in this collection steps aside
    vibes.forEach((button) => {
      if (!cards.some((card) => inVibe(card, button))) button.hidden = true;
    });

    // The gold pill slides under whichever option is chosen
    const moveThumb = () => {
      if (!segThumb || !who) return;
      segThumb.style.width = `${who.offsetWidth}px`;
      segThumb.style.transform = `translateX(${who.offsetLeft}px)`;
    };

    const paintRange = () => {
      const pct = ((budget() - floor) / Math.max(1, ceil - floor)) * 100;
      range.style.setProperty('--fill', `${pct}%`);
      rangeValue.textContent = isAny() ? range.dataset.any : `Up to ${money(budget())}`;
      range.setAttribute('aria-valuetext', rangeValue.textContent);
    };

    // The "Pick one for me" reveal: set up below; any re-render closes it first
    let picked = null;
    let leavePick = () => {};

    const render = (animateFrom = 0) => {
      leavePick();
      const matched = matches();
      // "Featured" puts bestsellers first, then keeps the collection's own order
      const featured = (a, b) => b.dataset.best - a.dataset.best || a.dataset.index - b.dataset.index;
      const byPrice = (a, b) => (Number(a.dataset.price) - Number(b.dataset.price)) * (sort === 'low' ? 1 : -1);
      matched.sort(sort === 'featured' ? featured : byPrice);
      const fragment = document.createDocumentFragment();
      matched.concat(cards.filter((card) => !matched.includes(card))).forEach((card) => fragment.appendChild(card));
      grid.appendChild(fragment);

      cards.forEach((card) => {
        card.hidden = true;
        card.classList.remove('is-entering');
      });
      matched.slice(0, limit).forEach((card, k) => {
        card.hidden = false;
        if (!reduced && k >= animateFrom) {
          card.style.setProperty('--k', (k - animateFrom) % 12);
          void card.offsetWidth;
          card.classList.add('is-entering');
        }
      });

      // "15 fragrances ✦ for him ✦ Fresh ✦ up to Dhs. 250"
      const parts = [`<b>${matched.length}</b> ${matched.length === 1 ? 'fragrance' : 'fragrances'}`];
      if (who && who.dataset.phrase) parts.push(`<span>${who.dataset.phrase}</span>`);
      if (vibe && vibe.dataset.phrase) parts.push(`<span>${vibe.dataset.phrase}</span>`);
      if (!isAny()) parts.push(`<span>up to ${money(budget())}</span>`);
      result.innerHTML = matched.length ? parts.join('<i aria-hidden="true">✦</i>') : '';

      const shown = Math.min(limit, matched.length);
      if (more) {
        more.hidden = matched.length <= shown;
        moreN.textContent = matched.length > shown ? `(${matched.length - shown})` : '';
      }
      empty.hidden = matched.length > 0;
      grid.dispatchEvent(new Event('scroll'));
      if (pick) pick.disabled = matched.length < 2;

      whoButtons.forEach((button) => button.setAttribute('aria-pressed', String(button === who)));
      vibes.forEach((button) => {
        const n = cards.filter((card) => inWho(card, who) && inVibe(card, button) && inBudget(card)).length;
        button.querySelector('[data-dw-n]').textContent = `${n} ${n === 1 ? 'fragrance' : 'fragrances'}`;
        button.classList.toggle('is-empty', n === 0);
        const on = button === vibe;
        button.setAttribute('aria-pressed', String(on));
        button.classList.toggle('is-lit', on);
      });
      moveThumb();
      return matched;
    };

    // After a pick, "Pick one for me" stays away until the shopper changes who it's for or the vibe
    let pickUsed = false;
    const offerPickAgain = () => {
      pickUsed = false;
      const button = section.querySelector('[data-dw-pick]');
      if (button) button.hidden = false;
    };

    whoButtons.forEach((button) =>
      button.addEventListener('click', () => {
        if (button === who) return;
        who = button;
        limit = step;
        offerPickAgain();
        render();
        toStart();
      })
    );
    vibes.forEach((button) =>
      button.addEventListener('click', () => {
        if (button === vibe) return;
        vibe = button;
        limit = step;
        offerPickAgain();
        render();
        toStart();
        sparkleAt(button.querySelector('.dw-diya__flame'), { count: 18, speed: 150, life: [0.4, 0.8], g: 260 });
      })
    );

    // Re-filter while dragging, but at most once a frame
    let queued = false;
    range.addEventListener('input', () => {
      paintRange();
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        limit = step;
        render(Infinity);
      });
    });
    range.addEventListener('change', () => {
      render();
      toStart();
    });

    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        sort = sortSelect.value;
        render();
        toStart();
      });
    }
    if (more) {
      more.addEventListener('click', () => {
        const from = limit;
        limit += step;
        render(from);
      });
    }
    if (reset) {
      reset.addEventListener('click', () => {
        who = whoButtons[0] || null;
        vibe = vibes.find((button) => !button.hidden) || null;
        range.value = String(ceil);
        sort = 'featured';
        if (sortSelect) sortSelect.value = 'featured';
        limit = step;
        paintRange();
        offerPickAgain();
        render();
        toStart();
      });
    }

    // "Pick one for me": the other matches step aside and the chosen perfume is revealed on its own, with the
    // reasons it suits this shopper: their choices (who, vibe, budget) and its own details (notes, saving).
    const match = section.querySelector('[data-dw-match]');
    const matchSlot = section.querySelector('[data-dw-match-card]');
    const matchTitle = section.querySelector('[data-dw-match-title]');
    const matchWhy = section.querySelector('[data-dw-match-why]');
    const matchNotes = section.querySelector('[data-dw-match-notes]');
    const pickAll = section.querySelector('[data-dw-pick-all]');
    const pickAllN = section.querySelector('[data-dw-pick-all-n]');
    const finderRail = section.querySelector('[data-dw-finder-rail]');
    const vibeName = (id) => {
      const button = vibes.find((v) => v.dataset.dwVibe === id);
      return button ? button.dataset.phrase : '';
    };

    const reasonsFor = (card) => {
      const groups = (card.dataset.for || '').split(' ');
      const reasons = [];
      const whoValue = who ? who.dataset.dwWho : 'all';
      if (groups.includes('gift')) reasons.push('Boxed and ready to gift');
      if (whoValue === 'him') reasons.push(groups.includes('any') ? 'Unisex, and loved by men' : 'Made for him');
      else if (whoValue === 'her') reasons.push(groups.includes('any') ? 'Unisex, and loved by women' : 'Made for her');
      else if (groups.includes('any')) reasons.push('Unisex: anyone will love it');
      else if (groups.includes('him')) reasons.push('A signature scent for him');
      else if (groups.includes('her')) reasons.push('A signature scent for her');
      const chosenVibe = vibe && vibe.dataset.dwVibe !== 'all' ? vibe.dataset.phrase : '';
      const ownVibe = vibeName((card.dataset.vibes || '').split(' ').filter(Boolean)[0] || '');
      if (chosenVibe) reasons.push(`Your vibe: ${chosenVibe}`);
      else if (ownVibe) reasons.push(`A ${ownVibe.toLowerCase()} fragrance`);
      if (!isAny()) reasons.push(`Within your ${money(budget())} budget`);
      if (card.dataset.best === '1') reasons.push('One of our bestsellers');
      if (Number(card.dataset.save) > 0) reasons.push(`You save ${money(Number(card.dataset.save))} this Diwali`);
      return reasons.slice(0, 4);
    };

    let pickHome = null;
    leavePick = () => {
      if (!picked) return;
      picked.classList.remove('is-picked');
      if (pickHome && pickHome.parent) pickHome.parent.insertBefore(picked, pickHome.next && pickHome.next.parentNode === pickHome.parent ? pickHome.next : null);
      picked = null;
      if (match) match.hidden = true;
      if (finderRail) finderRail.hidden = false;
      if (pick) pick.hidden = pickUsed;
      section.classList.remove('is-picking');
    };

    let lastPick = null;
    const pickOne = () => {
      const pool = cards.filter((card) => inWho(card, who) && inVibe(card, vibe) && inBudget(card));
      if (!pool.length || !match) return;
      let choice = pool[Math.floor(Math.random() * pool.length)];
      if (pool.length > 1) {
        while (choice === lastPick) choice = pool[Math.floor(Math.random() * pool.length)];
      }
      lastPick = choice;
      leavePick();

      // the chosen card moves into the reveal; the row of other matches and the picker step aside
      pickHome = { parent: choice.parentNode, next: choice.nextSibling };
      choice.hidden = false;
      choice.classList.remove('is-entering');
      matchSlot.appendChild(choice);
      picked = choice;
      pickUsed = true;
      section.classList.add('is-picking');
      if (finderRail) finderRail.hidden = true;
      if (pick) pick.hidden = true;
      match.hidden = false;

      const title = choice.querySelector('.dw-card__title');
      matchTitle.textContent = title ? title.textContent.trim() : '';
      matchWhy.innerHTML = reasonsFor(choice)
        .map((reason) => `<li><svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M4 10.5l4 4 8-9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg><span>${reason}</span></li>`)
        .join('');
      const notes = choice.dataset.notes || '';
      matchNotes.hidden = !notes;
      matchNotes.innerHTML = notes ? `<span>Smells of</span> ${notes}` : '';
      if (pickAllN) pickAllN.textContent = `(${pool.length})`;

      void choice.offsetWidth;
      choice.classList.add('is-picked');
      match.classList.remove('is-revealed');
      void match.offsetWidth;
      match.classList.add('is-revealed');
      match.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      setTimeout(() => sparkleAt(choice.querySelector('.dw-card__media'), { top: 0.45, count: 70, speed: 380, life: [0.7, 1.3], colors: ['gold', 'amber', 'ivory', 'rose'] }), reduced ? 0 : 650);
    };

    if (pick) {
      grid.style.setProperty('--dw-pick-tag', JSON.stringify(`${pick.dataset.tag || 'Picked for you'} ✦`));
      match && match.style.setProperty('--dw-pick-tag', JSON.stringify(`${pick.dataset.tag || 'Picked for you'} ✦`));
      pick.addEventListener('click', pickOne);
    }
    if (pickAll) {
      pickAll.addEventListener('click', () => {
        render();
        toStart();
        if (finderRail) finderRail.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      });
    }

    if (seg && 'ResizeObserver' in window) new ResizeObserver(moveThumb).observe(seg);
    window.addEventListener('resize', moveThumb, { passive: true });
    paintRange();
    render(Infinity);
    // reordering the cards makes the browser re-snap the row; start it at the beginning
    requestAnimationFrame(toStart);
    if (seg) requestAnimationFrame(() => seg.classList.add('is-ready'));
  }

  /* ---------- A Rangoli of Collections: dotted lines from the diya to each collection ---------- */

  function initRangoli(section) {
    const stage = section.querySelector('[data-dw-rangoli]');
    const svg = section.querySelector('[data-dw-rangoli-lines]');
    const core = section.querySelector('[data-dw-rangoli-core]');
    const items = Array.from(section.querySelectorAll('[data-dw-rangoli-item]'));
    if (!stage || !svg || !core || !items.length) return;
    const NS = 'http://www.w3.org/2000/svg';
    let lines = [];

    const draw = () => {
      svg.innerHTML = '';
      lines = [];
      if (getComputedStyle(svg).display === 'none') return;
      const box = stage.getBoundingClientRect();
      svg.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`);
      const cr = core.getBoundingClientRect();
      const cx = cr.left + cr.width / 2 - box.left;
      const cy = cr.top + cr.height / 2 - box.top;
      const inner = cr.width * 0.47;
      items.forEach((item, i) => {
        // aim at the medallion, but stop at the edge of the whole tile (medallion and name), so no line
        // ever runs through a collection's name
        const disc = item.querySelector('.dw-medal__disc').getBoundingClientRect();
        const tile = item.getBoundingClientRect();
        const ex = disc.left + disc.width / 2 - box.left;
        const ey = disc.top + disc.height / 2 - box.top;
        const dx = ex - cx;
        const dy = ey - cy;
        const len = Math.hypot(dx, dy) || 1;
        const ux = dx / len;
        const uy = dy / len;
        const left = tile.left - box.left - 10;
        const right = tile.right - box.left + 10;
        const top = disc.top - box.top - 18;
        const bottom = tile.bottom - box.top + 18;
        // walk back from the medallion's centre towards the diya until the line leaves the tile
        let back = 0;
        while (back < len) {
          const px = ex - ux * back;
          const py = ey - uy * back;
          if (px < left || px > right || py < top || py > bottom) break;
          back += 2;
        }
        const sx = cx + ux * inner;
        const sy = cy + uy * inner;
        const tx = ex - ux * back;
        const ty = ey - uy * back;
        const bend = (i % 2 ? 1 : -1) * 18;
        const mx = (sx + tx) / 2 - uy * bend;
        const my = (sy + ty) / 2 + ux * bend;
        const path = document.createElementNS(NS, 'path');
        path.setAttribute('d', `M${sx} ${sy}Q${mx} ${my} ${tx} ${ty}`);
        path.setAttribute('pathLength', '1');
        path.setAttribute('class', 'dw-rangoli__line');
        path.style.setProperty('--k', i);
        const dot = document.createElementNS(NS, 'circle');
        dot.setAttribute('cx', tx);
        dot.setAttribute('cy', ty);
        dot.setAttribute('r', '3');
        dot.setAttribute('class', 'dw-rangoli__dot');
        dot.style.setProperty('--k', i);
        svg.append(path, dot);
        lines.push(path);
      });
    };

    items.forEach((item, i) => {
      const link = item.querySelector('a');
      const hot = (on) => lines[i] && lines[i].classList.toggle('is-hot', on);
      link.addEventListener('mouseenter', () => hot(true));
      link.addEventListener('mouseleave', () => hot(false));
      link.addEventListener('focus', () => hot(true));
      link.addEventListener('blur', () => hot(false));
    });

    draw();
    if ('ResizeObserver' in window) new ResizeObserver(draw).observe(stage);
    else window.addEventListener('resize', draw, { passive: true });
    // the rangoli draws itself, the diya lights and the collections bloom once the section is in view
    watch(section, (isVisible) => isVisible && section.classList.add('is-lit'), { threshold: 0.25 });
  }

  /* ---------- Delivery tracker ---------- */

  const DAY = 86400000;
  // Noon, Indian time, on the calendar day that contains `time` (noon keeps day arithmetic clear of midnight)
  const istNoon = (time) => Date.parse(`${new Date(time).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })}T12:00:00+05:30`);
  const istWeekday = (time) => new Date(time).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'Asia/Kolkata' });
  const istDate = (time, options) => new Date(time).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', ...options });
  // Business days skip Sundays
  const addBusinessDays = (time, days) => {
    let day = time;
    let left = days;
    while (left > 0) {
      day += DAY;
      if (istWeekday(day) !== 'Sun') left -= 1;
    }
    return day;
  };

  function initPromise(section) {
    const route = section.querySelector('[data-dw-route]');
    if (!route) return;
    const data = section.dataset;
    const cutoff = parseDate(data.cutoff, NaN);
    const diwali = istNoon(parseDate(data.diwali, NaN));
    const dispatch = parseInt(data.dispatch, 10) || 0;
    const fastest = parseInt(data.min, 10) || 7;
    const slowest = Math.max(fastest, parseInt(data.max, 10) || 10);

    const today = istNoon(Date.now());
    const shipped = addBusinessDays(today, dispatch);
    const doorFrom = addBusinessDays(today, fastest);
    const doorBy = addBusinessDays(today, slowest);
    const daysToSpare = Math.round((diwali - doorBy) / DAY);
    const forDiwali = !Number.isNaN(diwali) && Date.now() <= cutoff && daysToSpare >= 0;

    const dayMonth = { day: 'numeric', month: 'short' };
    const set = (key, text) => {
      const el = section.querySelector(`[data-dw-date="${key}"]`);
      if (el) el.textContent = text;
    };
    set('today', istDate(today, dayMonth));
    set('ship', istDate(shipped, dayMonth));
    // "26–29 Oct", or "30 Oct – 2 Nov" across a month
    const sameMonth = istDate(doorFrom, { month: 'short' }) === istDate(doorBy, { month: 'short' });
    set('door', sameMonth ? `${istDate(doorFrom, { day: 'numeric' })}–${istDate(doorBy, dayMonth)}` : `${istDate(doorFrom, dayMonth)} – ${istDate(doorBy, dayMonth)}`);
    set('diwali', istDate(diwali, { weekday: 'short', ...dayMonth }));

    // The headline promises the slowest date, so it is always safe
    const title = section.querySelector('[data-dw-eta-title]');
    const sub = section.querySelector('[data-dw-eta-sub]');
    if (title && data.headline) title.textContent = data.headline.replace('[date]', istDate(doorBy, { weekday: 'short', ...dayMonth }));
    if (sub) {
      if (forDiwali && data.subline) {
        sub.textContent = data.subline.replace('[days] days', daysToSpare === 1 ? '1 day' : `${daysToSpare} days`).replace('[days]', daysToSpare);
      } else if (!forDiwali && data.sublineAfter) {
        sub.textContent = data.sublineAfter;
      }
    }

    // After the cutoff the Diwali stop and the timer step aside
    const diwaliStop = section.querySelector('[data-dw-diwali-stop]');
    if (diwaliStop) diwaliStop.hidden = !forDiwali;
    const stops = Array.from(route.querySelectorAll('.dw-route__stop')).filter((stop) => !stop.hidden);
    const doorIndex = stops.findIndex((stop) => stop.classList.contains('dw-route__stop--door'));
    route.style.setProperty('--n', stops.length);
    route.style.setProperty('--target', stops.length > 1 ? doorIndex / (stops.length - 1) : 0);
    stops.forEach((stop, i) => {
      stop.style.setProperty('--at', stops.length > 1 ? i / (stops.length - 1) : 0);
      stop.style.setProperty('--s', i);
    });

    // The lantern sets off along the route once it is on screen
    watch(route, (isVisible) => isVisible && route.classList.add('is-moving'), { threshold: 0.5 });

    // Time left to order for Diwali delivery
    const timer = section.querySelector('[data-dw-order-timer]');
    const left = section.querySelector('[data-dw-order-left]');
    if (timer) timer.hidden = !forDiwali;
    if (timer && left && forDiwali) {
      timer.hidden = false;
      const tick = () => {
        const ms = cutoff - Date.now();
        if (ms <= 0) {
          timer.hidden = true;
          return;
        }
        const minutes = Math.floor(ms / 60000);
        const d = Math.floor(minutes / 1440);
        const h = Math.floor((minutes % 1440) / 60);
        const m = minutes % 60;
        left.textContent = d > 0 ? `${d}d ${pad(h)}h ${pad(m)}m` : `${h}h ${pad(m)}m`;
      };
      tick();
      setInterval(tick, 20000);
    }
  }

  /* ---------- Finale fireworks ---------- */

  function initFinale(section) {
    const canvas = section.querySelector('[data-dw-fireworks]');
    if (!canvas || reduced) return;
    const sky = new Sky(canvas);
    let visible = false;
    let clock = 0.4;
    sky.keepAlive = () => visible;
    sky.update = (dt) => {
      if (!visible) return;
      clock -= dt;
      if (clock <= 0) {
        sky.launch();
        if (Math.random() < 0.3) setTimeout(() => sky.launch(), 220);
        clock = rand(0.8, 1.7);
      }
    };
    watch(
      section,
      (isVisible) => {
        visible = isVisible;
        if (visible) {
          sky.resize();
          sky.start();
        }
      },
      { threshold: 0.2 }
    );
    section.addEventListener('click', (e) => {
      if (e.target.closest('a, button')) return;
      const r = canvas.getBoundingClientRect();
      sky.launch(e.clientX - r.left, e.clientY - r.top);
    });
  }

  /* ---------- Sticky bar ---------- */

  function initSticky(bar) {
    const hide = new Set();
    const zones = [
      document.querySelector('.dw-hero'),
      document.querySelector('.dw-finale'),
      document.querySelector('footer, .footer, [id*="__footer"]')
    ].filter(Boolean);
    let seenHero = !document.querySelector('.dw-hero');
    const update = () => bar.classList.toggle('is-shown', seenHero && hide.size === 0);
    // A zone only counts once a fair share of it is on screen, so a sliver of the finale doesn't hide the bar
    zones.forEach((zone) =>
      watch(
        zone,
        (isVisible, entry) => {
          const inView = isVisible && (!entry || entry.intersectionRatio >= 0.2 || entry.intersectionRect.height > window.innerHeight * 0.35);
          if (inView) hide.add(zone);
          else {
            hide.delete(zone);
            if (zone.classList.contains('dw-hero')) seenHero = true;
          }
          update();
        },
        { threshold: [0, 0.1, 0.2, 0.3, 0.5] }
      )
    );
  }

  /* ---------- Add to bag: sparkle on tap, "Added ✓" once the cart has it ---------- */

  let lastAddButton = null;
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!form.closest || !form.closest('.dw-card')) return;
      lastAddButton = form.querySelector('.dw-btn--add');
      sparkleAt(lastAddButton);
    },
    true
  );
  document.addEventListener('product-ajax:added', () => {
    const button = lastAddButton;
    if (!button) return;
    button.classList.add('is-added');
    setTimeout(() => button.classList.remove('is-added'), 1800);
  });

  /* ---------- Boot (and re-boot sections changed in the theme editor) ---------- */

  const INITS = {
    hero: initHero,
    rail: initRail,
    gifts: initGifts,
    finder: initFinder,
    promise: initPromise,
    rangoli: initRangoli,
    finale: initFinale,
    sticky: initSticky
  };

  /* ---------- Header over the hero: its content starts below the floating header ---------- */

  function initHeaderOver() {
    const hero = document.querySelector('main > .shopify-section:first-child .dw-hero');
    const header = document.querySelector('header.header');
    if (!hero || !header) return;
    const measure = () => {
      const reach = header.getBoundingClientRect().bottom - hero.getBoundingClientRect().top;
      html.style.setProperty('--dw-header-h', `${Math.round(Math.max(0, reach))}px`);
    };
    measure();
    document.body.classList.add('dw-header-over');
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(header);
  }

  function boot(root) {
    root.querySelectorAll('[data-dw-section]').forEach((section) => {
      const init = INITS[section.dataset.dwSection];
      if (init) init(section);
    });
    initReveals(root);
    tickCountdowns();
  }

  const start = () => {
    initHeaderOver();
    boot(document);
    setInterval(tickCountdowns, 1000);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
